import {
  memberProfilePhotoResponseSchema,
  profilePhotoResponseSchema,
  type MemberProfilePhotoResponse,
  type ProfilePhotoResponse,
} from '@emr/shared';

import { apiRequest, apiUpload } from './client.ts';

export type { ProfilePhotoResponse, MemberProfilePhotoResponse };

/** `GET /api/profile/photo` — the caller's own current photo. Self-service, no capability. */
export function getOwnPhoto(signal?: AbortSignal): Promise<ProfilePhotoResponse> {
  return apiRequest(
    '/api/profile/photo',
    profilePhotoResponseSchema,
    signal === undefined ? {} : { signal },
  );
}

/** `POST /api/profile/photo` — set the caller's own photo. `file` must be a JPEG or PNG under
 * `@emr/shared`'s `MAX_PROFILE_PHOTO_BYTES`; the server re-checks both regardless of what this
 * client already knows, so a stale build enforcing an old limit never becomes the only guard. */
export function uploadOwnPhoto(file: File): Promise<ProfilePhotoResponse> {
  const formData = new FormData();
  formData.set('photo', file);
  return apiUpload('/api/profile/photo', profilePhotoResponseSchema, formData);
}

/** `DELETE /api/profile/photo` — clear the caller's own photo. */
export function deleteOwnPhoto(): Promise<ProfilePhotoResponse> {
  return apiRequest('/api/profile/photo', profilePhotoResponseSchema, { method: 'DELETE' });
}

/** `GET /api/orgs/members/:userId/photo` — admin-only (`org:manage_members`). */
export function getMemberPhoto(
  userId: string,
  signal?: AbortSignal,
): Promise<MemberProfilePhotoResponse> {
  return apiRequest(
    `/api/orgs/members/${userId}/photo`,
    memberProfilePhotoResponseSchema,
    signal === undefined ? {} : { signal },
  );
}

/** `POST /api/orgs/members/:userId/photo` — admin-only: set a colleague's photo. */
export function uploadMemberPhoto(userId: string, file: File): Promise<MemberProfilePhotoResponse> {
  const formData = new FormData();
  formData.set('photo', file);
  return apiUpload(`/api/orgs/members/${userId}/photo`, memberProfilePhotoResponseSchema, formData);
}

/** `DELETE /api/orgs/members/:userId/photo` — admin-only: clear a colleague's photo. */
export function deleteMemberPhoto(userId: string): Promise<MemberProfilePhotoResponse> {
  return apiRequest(`/api/orgs/members/${userId}/photo`, memberProfilePhotoResponseSchema, {
    method: 'DELETE',
  });
}
